/**
 * Source "Chat link": a URL template stored in `LeadSource.config.chatLink`
 * that opens a lead's conversation in whatever tool the admin points it at —
 * any link with a placeholder (e.g. `…&id={phone_with_countrycode_without+}`).
 * Nothing is tool-specific: every lead that came in through the source gets a
 * chat button in its header that opens the filled-in link — or, with
 * "every lead" ticked, every lead in the workspace (a chat tool that holds all
 * contacts, e.g. a WhatsApp inbox, isn't tied to the source a lead came from).
 *
 * No server code knows about it: the source editor sends `chatLink` with the
 * rest of the form and the server merges it into `config` like any other key.
 * That is also why the safety check lives HERE, at the moment of opening — the
 * stored value is whatever an admin (or a direct API call) put there.
 */

export type ChatLinkOpenIn = "window" | "inside";

export interface ChatLinkConfig {
  url: string;
  /**
   * "window" = a separate popup window (works with any site).
   * "inside" = a popup inside Xale (an iframe) — only works when the chat tool
   * allows other sites to show it (many send X-Frame-Options SAMEORIGIN).
   */
  openIn: ChatLinkOpenIn;
  /**
   * true = the button shows on EVERY lead, not just this source's. A lead whose
   * own source has a chat link still uses its own.
   */
  allLeads: boolean;
}

/** Source types whose editor offers the chat link. */
export const CHAT_LINK_SOURCE_TYPES = ["WEBSITE", "CUSTOM"] as const;

export function supportsChatLink(type: unknown): boolean {
  return (CHAT_LINK_SOURCE_TYPES as readonly string[]).includes(String(type ?? ""));
}

interface ChatLinkLead {
  id?: number | string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | number | null;
  phoneCountryCode?: string | null;
}

const digitsOf = (v: unknown) => String(v ?? "").replace(/\D/g, "");

/**
 * `Leads.phone` is the NATIONAL number and `phoneCountryCode` the `+<code>`;
 * legacy rows carry the whole number with its own "+" in `phone`.
 */
function phoneParts(lead: ChatLinkLead) {
  const raw = String(lead.phone ?? "").trim();
  if (raw.startsWith("+")) {
    const full = digitsOf(raw);
    return { national: full, countryCode: "", full };
  }
  const national = digitsOf(raw);
  const countryCode = digitsOf(lead.phoneCountryCode);
  return { national, countryCode, full: `${countryCode}${national}` };
}

/** The placeholders an admin can put in the link, with what each becomes. */
export const CHAT_LINK_PLACEHOLDERS: {
  token: string;
  label: string;
  value: (lead: ChatLinkLead) => string;
}[] = [
  {
    token: "{phone_with_countrycode_without+}",
    label: "Number with country code, no +",
    value: (l) => phoneParts(l).full,
  },
  {
    token: "{phone_with_countrycode}",
    label: "Number with +country code",
    value: (l) => {
      const { full } = phoneParts(l);
      return full ? `+${full}` : "";
    },
  },
  { token: "{phone}", label: "Number without country code", value: (l) => phoneParts(l).national },
  { token: "{country_code}", label: "Country code, no +", value: (l) => phoneParts(l).countryCode },
  { token: "{email}", label: "Email", value: (l) => String(l.email ?? "").trim() },
  { token: "{name}", label: "Name", value: (l) => String(l.name ?? "").trim() },
  { token: "{lead_id}", label: "Xale lead ID", value: (l) => String(l.id ?? "") },
];

/** A made-up lead for previews in the source editor. */
export const SAMPLE_CHAT_LINK_LEAD: ChatLinkLead = {
  id: 12345,
  name: "Student Name",
  email: "student@example.com",
  phone: "9876543210",
  phoneCountryCode: "+91",
};

/** Fills every placeholder (case-insensitive), URL-encoding each value. */
export function fillChatLink(template: string, lead: ChatLinkLead): string {
  let out = template.trim();
  for (const p of CHAT_LINK_PLACEHOLDERS) {
    const pattern = new RegExp(p.token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    out = out.replace(pattern, () => encodeURIComponent(p.value(lead)));
  }
  return out;
}

/**
 * Only http(s) links are ever opened — a `javascript:` or `data:` link in an
 * iframe or popup would run inside Xale's own origin.
 */
export function isSafeChatLink(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

/** The template as the editor validates it: filled with the sample lead. */
export function isValidChatLinkTemplate(template: string): boolean {
  return isSafeChatLink(fillChatLink(template, SAMPLE_CHAT_LINK_LEAD));
}

/** `config.chatLink` → a usable link, or null when unset/unsafe. */
export function readChatLink(config: unknown): ChatLinkConfig | null {
  const raw = (config as { chatLink?: unknown } | null | undefined)?.chatLink as
    | { url?: unknown; openIn?: unknown; allLeads?: unknown }
    | null
    | undefined;
  const url = typeof raw?.url === "string" ? raw.url.trim() : "";
  if (!url || !isValidChatLinkTemplate(url)) return null;
  return {
    url,
    openIn: raw?.openIn === "inside" ? "inside" : "window",
    allLeads: raw?.allLeads === true,
  };
}

interface ChatLinkSourceRow {
  id: number | string;
  isActive?: boolean | null;
  config?: unknown;
}

/**
 * The source whose chat link a lead's button opens: the lead's OWN source when
 * it has one, else the first active source marked "every lead" (lowest id, so
 * every user lands on the same one).
 */
export function pickChatLinkSource<S extends ChatLinkSourceRow>(
  sources: readonly S[] | null | undefined,
  leadSourceId: number | string | null | undefined,
): { source: S; chatLink: ChatLinkConfig } | null {
  const rows = sources ?? [];
  if (leadSourceId != null) {
    const own = rows.find((s) => Number(s?.id) === Number(leadSourceId));
    const ownLink = own ? readChatLink(own.config) : null;
    if (own && ownLink) return { source: own, chatLink: ownLink };
  }
  let best: { source: S; chatLink: ChatLinkConfig } | null = null;
  for (const s of rows) {
    if (s?.isActive === false) continue;
    const link = readChatLink(s?.config);
    if (!link?.allLeads) continue;
    if (!best || Number(s.id) < Number(best.source.id)) best = { source: s, chatLink: link };
  }
  return best;
}

/**
 * Opens (or re-uses) ONE chat window per source, docked on the right of the
 * screen, so moving to the next lead and clicking again swaps the chat in the
 * same window instead of piling up tabs.
 *
 * The window is opened blank first so its `opener` can be cut while it is
 * still same-origin; the chat page then cannot navigate Xale's tab.
 */
export function openChatWindow(url: string, sourceId: number | string): boolean {
  if (!isSafeChatLink(url)) return false;
  const width = Math.min(520, window.screen.availWidth);
  const height = Math.max(480, window.screen.availHeight - 80);
  const left = Math.max(0, window.screen.availWidth - width - 16);
  const features = `popup=yes,width=${width},height=${height},left=${left},top=40`;
  const win = window.open("", `xale-chat-${sourceId}`, features);
  if (!win) return false;
  try {
    win.opener = null;
  } catch {
    // Already showing the chat site from an earlier click (cross-origin):
    // its opener was cut the first time.
  }
  win.location.href = url;
  win.focus();
  return true;
}
